import axios, { AxiosResponse } from "axios";
import { LocationType } from "../../Presentation/Components/SearchHeader/ListItem/ListItemLocation.types";
import SearchIcon from "@mui/icons-material/Search";
import react from "react";
import ApiService from "./ApiService";
import {
    GooglePlaceOptions,
    LatLng,
    SearchBounds,
    SearchItemPlaceResult,
} from "./GooglePlaceService.types";
import * as client from "../ClientConsumer";
import { generatePath } from "react-router-dom";

const routes = {
    GET_SEARCH_PLACES: "/community/search/places",
    GET_PLACE_DETAILS: "/community/search/places/:placeId",
};

class GooglePlaceService {
    private options: GooglePlaceOptions;
    private logger: Console;

    constructor(options: GooglePlaceOptions, logger: Console) {
        this.options = options;
        this.logger = logger;
    }

    public async searchForPlaces(
        search?: string,
        location?: GeolocationPosition | undefined | null,
        groupId?: string | undefined | null,
        bounds?: SearchBounds,
    ): Promise<LocationType[]> {
        return this.callApi(location, search, groupId, bounds).then(
            function (data) {
                const searchIcon = react.createElement(SearchIcon);
                return data.map((location: SearchItemPlaceResult) => {
                    return {
                        id: location.id,
                        headline: location.name,
                        description: location.description,
                        icon: searchIcon,
                        latitude: location.lat,
                        longitude: location.lng,
                    } as unknown as LocationType;
                });
            },
        );
    }

    private async callApi(
        location?: GeolocationPosition | null,
        search: string | undefined,
        groupId?: string | undefined | null,
        bounds: SearchBounds | undefined,
    ) {
        return await axios
            .get(
                this.buildURL(
                    {
                        lat: location?.coords.latitude,
                        lng: location?.coords.longitude,
                    } as LatLng,
                    search,
                    groupId,
                    bounds,
                ),
            )
            .then((response: AxiosResponse<any>) => {
                if (response.data.error) {
                    throw new Error(response.data);
                }
                return response.data;
            })
            .catch((error) => {
                this.logger.error(error.message);
            });
    }

    private buildURL(
        location?: LatLng,
        search?: string,
        groupId?: string | undefined | null,
        bounds?: SearchBounds,
    ): string {
        const query = [];
        if (location?.lat !== undefined && location?.lng !== undefined) {
            query.push(`location=${location.lat},${location.lng}`);
        }
        if (search !== undefined) {
            //TODO: Sanitize string
            query.push(`q=${search}`);
        }
        if (groupId !== undefined) {
            query.push(`groupId=${groupId}`);
        }
        if (bounds !== undefined) {
            query.push(
                `bounds[sw]=${bounds.pointSW.lng},${bounds.pointSW.lat}`,
            );
            query.push(
                `bounds[ne]=${bounds.pointNE.lng},${bounds.pointNE.lat}`,
            );
        }
        const finalHttp = `${this.options.url}${
            query.length ? "?" + query.join("&") : ""
        }`;
        this.logger.debug(`URL: ${finalHttp}`);
        return finalHttp;
    }
}

const options = {
    url: `${ApiService.BASE_URL}${routes.GET_SEARCH_PLACES}`,
} as GooglePlaceOptions;

export default new GooglePlaceService(options, console);

type PlaceDetailsType = {
    description: string;
    icon: string;
    id: string;
    lat: number;
    lng: number;
    name: string;
};

export function getPlaceDetails(placeId: string): Promise<PlaceDetailsType> {
    return client
        .getData(
            generatePath(routes.GET_PLACE_DETAILS, {
                placeId: placeId,
            }),
        )
        .then((res) => {
            return res.data;
        });
}
